"use client";

import { useEffect, useRef, useState } from "react";
import { useTranslations } from "next-intl";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./dialog";
import { Loading } from "./loading";
import { Pagination } from "./pagination";
import { api_url } from "@/app/api";
import { fetchWithAuth } from "@/app/lib/authInterceptor";
import { CONFIG_KEYS } from "@/app/store/chat";
import { PaginationInfo, Version } from "@/types";

export interface VersionModalProps {
   isOpen: boolean;
   onClose: () => void;
   versions?: Version[];
   pageSize?: number;
}

type VersionRequestState = {
   key: string;
   status: "loading" | "ready" | "error";
   items: Version[];
   pagination: PaginationInfo | null;
};

const INITIAL_REQUEST_STATE: VersionRequestState = {
   key: "",
   status: "loading",
   items: [],
   pagination: null
};

export function VersionModal({
   isOpen,
   onClose,
   versions,
   pageSize = 10
}: VersionModalProps) {
   const translation = useTranslations("AppVersion");
   const isControlledVersions = versions !== undefined;
   const [page, setPage] = useState(1);
   const [requestState, setRequestState] =
      useState<VersionRequestState>(INITIAL_REQUEST_STATE);
   const listContainerRef = useRef<HTMLDivElement>(null);
   const activeRequestKey = `${page}:${pageSize}`;

   useEffect(() => {
      if (isControlledVersions) {
         return;
      }
      if (!isOpen) {
         setPage(1);
         setRequestState(INITIAL_REQUEST_STATE);
         return;
      }

      const controller = new AbortController();
      const loadVersionPage = async () => {
         try {
            const response = await fetchWithAuth(
               `${api_url.config_url}?key=${CONFIG_KEYS.appVersion}&page=${page}&limit=${pageSize}`,
               { signal: controller.signal }
            );
            if (!response.ok) {
               throw new Error(`Request failed with status ${response.status}`);
            }
            const payload: {
               status?: string;
               data?: Version[];
               pagination?: PaginationInfo;
            } = await response.json();
            if (payload?.status !== "success") {
               throw new Error("Failed to load version history");
            }
            if (controller.signal.aborted) {
               return;
            }
            setRequestState({
               key: activeRequestKey,
               status: "ready",
               items: Array.isArray(payload.data) ? payload.data : [],
               pagination: payload.pagination ?? null
            });
         } catch (error) {
            const isAborted =
               controller.signal.aborted ||
               (error instanceof Error && error.name === "AbortError");
            if (isAborted) {
               return;
            }
            console.error("[VersionModal] Failed to load version history:", {
               page,
               pageSize,
               error
            });
            setRequestState({
               key: activeRequestKey,
               status: "error",
               items: [],
               pagination: null
            });
         }
      };

      loadVersionPage();

      return () => {
         controller.abort();
      };
   }, [activeRequestKey, isControlledVersions, isOpen, page, pageSize]);

   const handlePageChange = (nextPage: number) => {
      setPage(nextPage);
      if (listContainerRef.current) {
         if (typeof listContainerRef.current.scrollTo === "function") {
            listContainerRef.current.scrollTo({ top: 0, behavior: "auto" });
         }
         listContainerRef.current.scrollTop = 0;
      }
   };

   const isCurrentRequestResolved = requestState.key === activeRequestKey;
   const isLoading =
      !isControlledVersions &&
      isOpen &&
      (!isCurrentRequestResolved || requestState.status === "loading");
   const hasError =
      !isControlledVersions &&
      isCurrentRequestResolved &&
      requestState.status === "error";
   const fetchedVersions = isCurrentRequestResolved ? requestState.items : [];
   const displayedVersions = isControlledVersions ? versions : fetchedVersions;
   const canShowPagination = !isControlledVersions && !isLoading && !hasError;
   const activePagination = canShowPagination ? requestState.pagination : null;

   const renderVersionBody = () => {
      if (isLoading) {
         return (
            <div className="py-10">
               <Loading />
            </div>
         );
      }
      if (hasError) {
         return (
            <p className="py-10 text-center text-sm text-destructive">
               {translation("loadError")}
            </p>
         );
      }
      if (displayedVersions.length === 0) {
         return (
            <p className="py-10 text-center text-sm text-muted-foreground">
               {translation("emptyHistory")}
            </p>
         );
      }
      return displayedVersions.map((version) => (
         <div key={version.version} className="space-y-2">
            <div className="flex items-center gap-2">
               <h3 className="font-semibold">Version {version.version}</h3>
               <span className="text-muted-foreground text-sm">{version.date}</span>
            </div>

            <ul className="list-none space-y-1">
               {version?.changes?.map((change, index) => {
                  const trimmedLink = change.link?.trim() ?? "";
                  const isHttpLink = /^https?:\/\//i.test(trimmedLink);
                  const isRelativePathLink =
                     trimmedLink.startsWith("/") && !trimmedLink.startsWith("//");
                  const hasSafeDetailLink = isHttpLink || isRelativePathLink;
                  return (
                     <li key={index} className="text-sm">
                        {change.text}
                        {hasSafeDetailLink && (
                           <a
                              href={trimmedLink}
                              className="text-blue-500 ml-1"
                              target="_blank"
                              rel="noopener noreferrer"
                           >
                              {`=> detail`}
                           </a>
                        )}
                     </li>
                  );
               })}
            </ul>
         </div>
      ));
   };

   return (
      <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
         <DialogContent className="max-w-xl h-[90vh] flex flex-col">
            <DialogHeader className="shrink-0">
               <DialogTitle className="text-lg font-bold">
                  {translation("updateHistory")}
               </DialogTitle>
            </DialogHeader>

            <div
               ref={listContainerRef}
               className="mt-4 flex-1 min-h-0 space-y-6 overflow-auto"
            >
               {renderVersionBody()}
            </div>

            {activePagination && activePagination.totalPages > 1 && (
               <div className="pt-4 border-t flex justify-center shrink-0">
                  <Pagination
                     pagination={activePagination}
                     handlePageChange={handlePageChange}
                  />
               </div>
            )}
         </DialogContent>
      </Dialog>
   );
}
